import { useState } from 'react';
import ColourPalette from '../components/lab/ColourPalette/ColourPalette';
import ROICalculator from '../components/lab/ROICalculator/ROICalculator';
import CostEstimator from '../components/lab/CostEstimator/CostEstimator';
import SpeedTest from '../components/lab/SpeedTest/SpeedTest';
import BuildAScreen from '../components/lab/BuildAScreen/BuildAScreen';
import styles from './Lab.module.css';

const TOOLS = [
  {
    id: 'palette',
    label: 'Colour Palette',
    description: 'Generate harmonious palettes and check WCAG contrast from any brand colour',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M12 3c-4.97 0-9 4.03-9 9s4.03 9 9 9c.83 0 1.5-.67 1.5-1.5 0-.39-.15-.74-.39-1.01-.23-.26-.38-.61-.38-.99 0-.83.67-1.5 1.5-1.5H16c2.76 0 5-2.24 5-5 0-4.42-4.03-8-9-8zm-5.5 9c-.83 0-1.5-.67-1.5-1.5S5.67 9 6.5 9 8 9.67 8 10.5 7.33 12 6.5 12zm3-4C8.67 8 8 7.33 8 6.5S8.67 5 9.5 5s1.5.67 1.5 1.5S10.33 8 9.5 8zm5 0c-.83 0-1.5-.67-1.5-1.5S13.67 5 14.5 5s1.5.67 1.5 1.5S15.33 8 14.5 8zm3 4c-.83 0-1.5-.67-1.5-1.5S16.67 9 17.5 9s1.5.67 1.5 1.5S18.33 12 17.5 12z"/>
      </svg>
    ),
    component: ColourPalette,
  },
  {
    id: 'roi',
    label: 'ROI Calculator',
    description: 'Calculate the annual return on automating your most repetitive tasks',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z"/>
      </svg>
    ),
    component: ROICalculator,
  },
  {
    id: 'cost',
    label: 'Cost Estimator',
    description: 'Get a rough build-cost range for your app idea in a few quick steps',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM8 17H6v-2h2v2zm0-4H6v-2h2v2zm0-4H6V7h2v2zm6 8h-4v-2h4v2zm0-4h-4v-2h4v2zm0-4h-4V7h4v2zm4 8h-2v-2h2v2zm0-4h-2v-2h2v2zm0-4h-2V7h2v2z"/>
      </svg>
    ),
    component: CostEstimator,
  },
  {
    id: 'speed',
    label: 'Speed Test',
    description: "Check any website's Google PageSpeed score across four key dimensions",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M7 2v11h3v9l7-12h-4l4-8z"/>
      </svg>
    ),
    component: SpeedTest,
  },
  {
    id: 'builder',
    label: 'Build-a-Screen',
    description: 'Prototype a UI layout live — pick a template, choose colours, and preview instantly',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4V6h16v12zM6 10h2v2H6zm0 4h8v2H6zm4-4h8v2h-8z"/>
      </svg>
    ),
    component: BuildAScreen,
  },
];

const Lab = () => {
  const [activeTool, setActiveTool] = useState('palette');
  const ActiveTool = TOOLS.find(t => t.id === activeTool);

  return (
    <div className={styles.page}>
      <div className={styles.hero}>
        <div className="section-label">Interactive Tools</div>
        <h1 className={styles.heroTitle}>The Lab</h1>
        <p className={styles.heroSub}>
          Free tools to explore ideas, validate concepts, and understand what building with us looks like.
        </p>
      </div>

      <div className={styles.toolBar}>
        <div className={styles.toolBarInner}>
          {TOOLS.map(tool => (
            <button
              key={tool.id}
              className={`${styles.toolTab} ${activeTool === tool.id ? styles.toolTabActive : ''}`}
              onClick={() => setActiveTool(tool.id)}
            >
              <span className={styles.toolTabIcon}>{tool.icon}</span>
              <span className={styles.toolTabLabel}>{tool.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className={styles.toolArea}>
        <div className={styles.toolHeader}>
          <h2 className={styles.toolTitle}>{ActiveTool?.label}</h2>
          <p className={styles.toolDescription}>{ActiveTool?.description}</p>
        </div>
        <div className={styles.toolContent}>
          {ActiveTool && <ActiveTool.component key={activeTool} />}
        </div>
      </div>
    </div>
  );
};

export default Lab;
